import { useEffect, useRef, useState } from "react";

import { Dialog } from "react-aria-components";

import { Button } from "#components/Button";
import { CaretRightIcon } from "#icons";
import { cn } from "#utils";

interface Option {
  key: number;
  label: string;
}

interface NoFocusSelectProps {
  options: Option[];
  selected: number;
  onClick: (n: number) => void;
  className?: string;
}

export function NoFocusSelect({ options, selected, onClick, className }: NoFocusSelectProps) {
  const [showPopover, setShowPopover] = useState(false);
  const selectedOption = options.find((o) => o.key === selected) || options[0];
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener("click", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [showPopover]);

  return (
    <div className="scrollbar-custom relative" ref={ref}>
      <Button
        className="flex items-center gap-sm"
        onPress={() => setShowPopover(!showPopover)}
        variant="tertiary"
      >
        {selectedOption.label}
        <CaretRightIcon className={cn("h-4 w-4", showPopover ? "-rotate-90" : "rotate-90")} />
      </Button>
      {showPopover ? (
        <Dialog
          aria-label="no-focus-select-dialog"
          className={cn(
            "absolute top-xl z-10 min-w-[10rem] rounded-md border border-outline-input-enabled bg-background-secondary px-1 py-2 shadow-md outline-none",
            className,
          )}
        >
          <div className="max-h-[14rem] overflow-y-auto">
            {options.map((option) => (
              <Button
                className="w-full"
                key={`option-${option.key}`}
                onPress={() => {
                  onClick(option.key);
                  setShowPopover(false);
                }}
                variant="dropdown"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
